import { useEffect, useRef, useState } from 'react';
import { ChevronDown, CrossIcon } from 'assets/icons';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import AiSuggestedMark from './AiSuggestedMark';
import {
  REFERENTIAL_STATUS,
  requirementNameKey,
} from './qualificationRequirements';
import { qualificationText } from './qualificationTexts';

const requirementsText = (key, values) =>
  qualificationText(`requirements.${key}`, values);

const toSearchable = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

/**
 * A multi-choice list of the names of a collection, the chosen ones shown as
 * removable pills below. The options stay open while boxes are checked.
 */
const RequirementPicker = ({
  id,
  label,
  placeholder,
  listError,
  options,
  selected,
  onToggle,
  onRemove,
  footer,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const labelId = `${id}-label`;
  const placeholderId = `${id}-placeholder`;
  const optionsId = `${id}-options`;
  const isReady = options.status === REFERENTIAL_STATUS.READY;

  useEffect(() => {
    if (!isOpen) return undefined;
    searchRef.current?.focus();
    const closeOnOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [isOpen]);

  const close = () => {
    setIsOpen(false);
    setSearch('');
    triggerRef.current?.focus();
  };

  const selectedKeys = new Set(
    selected.map((item) => requirementNameKey(item.name)),
  );
  const wanted = toSearchable(search);
  const visibleOptions = options.items.filter((name) =>
    toSearchable(name).includes(wanted),
  );

  return (
    <div className="flex flex-col gap-2">
      <span id={labelId} className="text-sm text-gray-500">
        {label}
      </span>
      <div
        ref={containerRef}
        className="relative"
        onKeyDown={(event) => {
          if (event.key === 'Escape' && isOpen) {
            // The slide-over must not see the Escape that closes the list.
            event.stopPropagation();
            close();
          }
        }}
      >
        <button
          ref={triggerRef}
          type="button"
          aria-labelledby={`${labelId} ${placeholderId}`}
          aria-expanded={isOpen}
          aria-controls={optionsId}
          disabled={!isReady}
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span id={placeholderId} className="truncate text-gray-400">
            {options.status === REFERENTIAL_STATUS.LOADING
              ? requirementsText('loading')
              : placeholder}
          </span>
          <span aria-hidden="true" className="shrink-0">
            <ChevronDown wh={20} color="#374151" />
          </span>
        </button>
        {isOpen && (
          <div
            id={optionsId}
            className="absolute left-0 right-0 top-full z-20 mt-1 flex flex-col gap-2 rounded-md border border-gray-200 bg-white p-2 shadow-lg"
          >
            <input
              ref={searchRef}
              type="search"
              aria-label={requirementsText('search')}
              placeholder={requirementsText('search')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <fieldset className="min-w-0">
              <legend className="sr-only">{label}</legend>
              {visibleOptions.length > 0 ? (
                <ul className="flex max-h-56 flex-col overflow-y-auto">
                  {visibleOptions.map((name) => (
                    <li key={name}>
                      <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-gray-900 hover:bg-gray-50">
                        <input
                          type="checkbox"
                          className="checkbox-primary shrink-0"
                          checked={selectedKeys.has(requirementNameKey(name))}
                          onChange={() => onToggle(name)}
                        />
                        <span className="min-w-0 break-words">{name}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-2 py-1.5 text-sm text-gray-500">
                  {requirementsText('noResult')}
                </p>
              )}
            </fieldset>
          </div>
        )}
      </div>
      {options.status === REFERENTIAL_STATUS.FAILED && (
        <p data-testid={`${id}-list-error`} className="text-xs text-red-600">
          {listError}
        </p>
      )}
      {selected.length > 0 && (
        <ul aria-labelledby={labelId} className="flex flex-wrap gap-2">
          {selected.map((item) => (
            <li
              key={item.name}
              data-testid={`${id}-pill`}
              data-ai={item.source === 'ai' ? 'true' : 'false'}
              className="flex items-center gap-1 rounded-md bg-gray-100 py-0.5 pl-2 pr-1 text-sm font-medium text-gray-900"
            >
              <span>{item.name}</span>
              {item.source === 'ai' && <AiSuggestedMark />}
              <IconTooltip label={requirementsText('removeTooltip')}>
                <button
                  type="button"
                  aria-label={requirementsText('remove', { name: item.name })}
                  onClick={() => onRemove(item.name)}
                  className="flex h-5 w-5 items-center justify-center rounded hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500"
                >
                  <CrossIcon wh={14} color="#6B7280" />
                </button>
              </IconTooltip>
            </li>
          ))}
        </ul>
      )}
      {footer}
    </div>
  );
};

/** EPI (tools collection) and licences (license collection) of the job. */
const QualificationRequirementsTab = ({
  tools,
  licences,
  toolOptions,
  licenceOptions,
  onToolToggle,
  onToolRemove,
  onLicenceToggle,
  onLicenceRemove,
  toolsFooter,
  licencesFooter,
}) => (
  <div className="flex flex-col gap-6">
    <RequirementPicker
      id="qualification-tools"
      label={requirementsText('tools')}
      placeholder={requirementsText('toolsPlaceholder')}
      listError={requirementsText('toolsListError')}
      options={toolOptions}
      selected={tools}
      onToggle={onToolToggle}
      onRemove={onToolRemove}
      footer={toolsFooter}
    />
    <RequirementPicker
      id="qualification-licences"
      label={requirementsText('licences')}
      placeholder={requirementsText('licencesPlaceholder')}
      listError={requirementsText('licencesListError')}
      options={licenceOptions}
      selected={licences}
      onToggle={onLicenceToggle}
      onRemove={onLicenceRemove}
      footer={licencesFooter}
    />
  </div>
);

export default QualificationRequirementsTab;
